/* Day · Week · Month · Year, the way every calendar does it.
 *
 * These four are not four sections. They are four distances from the same
 * thing, and a person switching between them is not navigating — they are
 * zooming. Four tabs in the bar would say otherwise, and would push Tasks and
 * Balance off the edge of a phone besides.
 *
 * So the tab bar holds one Calendar entry and this sits at the top of whichever
 * of the four is open, exactly where Android and iOS put it. The date carries
 * across: switching from a Tuesday in the day view to the week shows the week
 * containing that Tuesday, not the current one, because the whole point of
 * changing distance is to look at the *same* thing from further away.
 *
 * It is a `tablist` rather than four links so a screen reader announces it as
 * one control with four options, and the arrow keys move between them.
 */

import { useState } from 'react';
import { NavLink, useLocation, useSearchParams } from 'react-router-dom';
import MapOut from './MapOut';
import Icon from './Icon';
import { useSession, localToday } from '../session';

const VIEWS = [
  /* `ahead` is how far "map out" reaches from each distance. Looking at a
     month and being offered to plan the next three days would be answering a
     question nobody asked. */
  { to: '/day', label: 'Day', ahead: 3 },
  { to: '/week', label: 'Week', ahead: 7 },
  { to: '/month', label: 'Month', ahead: 14 },
  { to: '/year', label: 'Year', ahead: 30 }
];

export default function ViewSwitch() {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const { user, today } = useSession();
  const [mapping, setMapping] = useState(false);

  /* The day being looked at, carried between the four. Without this, opening
     the week from a Tuesday in November lands you on this week — which reads
     as the app losing your place. */
  const date = params.get('date');
  const href = (to: string) => (date ? `${to}?date=${date}` : to);

  const here = VIEWS.find(v => v.to === pathname) ?? VIEWS[0];
  /* Map out from the day being looked at, not from today: somebody looking at
     next week means next week. */
  const from = date ?? today ?? localToday(user?.tz);

  return (
    <div className="viewbar">
      <div className="viewswitch" role="tablist" aria-label="Calendar view">
        {VIEWS.map(v => (
          <NavLink
            key={v.to}
            to={href(v.to)}
            role="tab"
            aria-selected={pathname === v.to}
            className={`viewswitch-tab${pathname === v.to ? ' is-active' : ''}`}>
            {v.label}
          </NavLink>
        ))}
      </div>

      {/* On every distance, because the answer to "when does this all happen"
          is worth asking from wherever you noticed the question. */}
      <button className="btn btn-ghost btn-sm" onClick={() => setMapping(true)}
              title="Plan the days ahead in one go">
        <Icon name="map" size={16} /><span className="viewbar-label">Map out ahead</span>
      </button>

      {mapping && (
        <MapOut from={from} defaultDays={here.ahead} onClose={() => setMapping(false)} />
      )}
    </div>
  );
}
